import React, { useEffect, useRef, useState } from 'react';
import './LiveTalk.css';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Slide-in drawer chat interface for talking with a single Signal agent.
 * Plays the reply through OpenAI TTS in the agent's assigned voice.
 */
export default function LiveTalkDrawer({ open, onClose, agent, accent = '#a78bfa', accent2 = '#7dd3fc' }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [autoTts, setAutoTts] = useState(true);
  const [playingId, setPlayingId] = useState(null);
  const [speakingState, setSpeakingState] = useState('idle'); // idle | loading | playing | error
  const [lastModel, setLastModel] = useState('');
  const audioRef = useRef(null);
  const streamRef = useRef(null);
  const inputRef = useRef(null);
  const ttsAbortRef = useRef(null);
  const playTokenRef = useRef(0);

  // Scroll to bottom on new message
  useEffect(() => {
    if (streamRef.current) {
      streamRef.current.scrollTop = streamRef.current.scrollHeight;
    }
  }, [messages, busy]);

  // Reset on open/agent change
  useEffect(() => {
    if (open) {
      setMessages([]);
      setInput('');
      setLastModel('');
      stopAudio();
      setTimeout(() => inputRef.current?.focus(), 240);
    } else {
      stopAudio();
    }
  }, [open, agent]);

  // Esc to close
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const stopAudio = () => {
    playTokenRef.current += 1;
    if (ttsAbortRef.current) {
      try { ttsAbortRef.current.abort(); } catch (_e) { /* no-op */ }
      ttsAbortRef.current = null;
    }
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch (_e) { /* no-op */ }
      audioRef.current = null;
    }
    setPlayingId(null);
    setSpeakingState('idle');
  };

  const normalizeRuntimeMessage = (message, fallback = 'Signal voice runtime is degraded.') => {
    const value = String(message || '').trim();
    if (!value) return fallback;
    if (value.includes('RuntimeError') || value.includes('Traceback') || value.includes('HTTPStatusError')) {
      return fallback;
    }
    return value;
  };

  const chunkForSpeech = (text) => {
    const parts = String(text || '')
      .split(/(?<=[.!?])\s+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (parts.length <= 1) return [String(text || '').trim()].filter(Boolean);
    const chunks = [];
    let buffer = '';
    parts.forEach((part) => {
      const candidate = buffer ? `${buffer} ${part}` : part;
      if (candidate.length > 280) {
        if (buffer) chunks.push(buffer);
        buffer = part;
      } else {
        buffer = candidate;
      }
    });
    if (buffer) chunks.push(buffer);
    return chunks;
  };

  const playBlob = (blob, token) => new Promise((resolve, reject) => {
    if (token !== playTokenRef.current) return resolve();
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    audioRef.current = audio;
    const cleanup = () => {
      URL.revokeObjectURL(url);
      if (audioRef.current === audio) audioRef.current = null;
    };
    audio.onended = () => { cleanup(); resolve(); };
    audio.onerror = () => { cleanup(); reject(new Error('audio playback failed')); };
    audio.play().then(() => {
      if (token === playTokenRef.current) setSpeakingState('playing');
    }).catch((err) => { cleanup(); reject(err); });
  });

  const playTts = async (msgId, text) => {
    stopAudio();
    if (!text) return;
    const token = playTokenRef.current;
    setPlayingId(msgId);
    setSpeakingState('loading');
    try {
      const chunks = chunkForSpeech(text);
      for (const chunk of chunks) {
        if (token !== playTokenRef.current) return;
        const controller = new AbortController();
        ttsAbortRef.current = controller;
        const res = await fetch(`${API}/api/voice/tts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ agent, text: chunk }),
          signal: controller.signal,
        });
        if (ttsAbortRef.current === controller) ttsAbortRef.current = null;
        if (!res.ok) throw new Error(`tts ${res.status}`);
        const blob = await res.blob();
        await playBlob(blob, token);
      }
      if (token === playTokenRef.current) {
        setPlayingId(null);
        setSpeakingState('idle');
      }
    } catch (e) {
      if (e?.name === 'AbortError') return;
      console.error('tts failed', e);
      setPlayingId(null);
      setSpeakingState('error');
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    stopAudio(); // prevent overlap with previous speech
    const userMsg = { id: `u-${Date.now()}`, role: 'user', text };
    setMessages((m) => [...m, userMsg]);
    setInput('');
    setBusy(true);
    try {
      // Build short history for the API: last 8 turns, in voice_router shape.
      const history = messages.slice(-8).map((m) => ({
        from_agent: m.role === 'user' ? 'operator' : agent,
        text: m.text,
      }));
      const res = await fetch(`${API}/api/voice/say`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agent, user_input: text, history }),
      });
      const data = await res.json();
      if (!res.ok) {
        const detail = data?.detail;
        const providerMissing = typeof detail === 'object' && detail?.error === 'NO_LLM_PROVIDER_CONFIGURED';
        const message = providerMissing
          ? 'No active LLM provider configured.'
          : (typeof detail === 'string' ? detail : detail?.message) || `voice error (${res.status})`;
        throw new Error(normalizeRuntimeMessage(message));
      }
      const replyText = data.text || '[no reply]';
      const replyId = `a-${Date.now()}`;
      setMessages((m) => [...m, {
        id: replyId, role: 'agent', text: replyText,
        provider: data.provider, model: data.model, fallback_used: data.fallback_used,
      }]);
      setLastModel(`${data.provider}/${data.model}${data.fallback_used ? ' · Provider fallback active' : ''}`);
      if (autoTts) {
        // Fire-and-forget; don't block UI on TTS
        playTts(replyId, replyText);
      }
    } catch (e) {
      const replyId = `e-${Date.now()}`;
      setMessages((m) => [...m, { id: replyId, role: 'agent', text: `[error: ${normalizeRuntimeMessage(e.message)}]` }]);
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  if (!open) return null;
  const initials = (agent || '?').slice(0, 2).toUpperCase();
  const cssVars = {
    '--talk-accent': accent,
    '--talk-accent-2': accent2,
    '--talk-accent-soft': `${accent}33`,
  };

  return (
    <>
      <div className="live-talk-backdrop" onClick={onClose} data-testid="live-talk-backdrop" />
      <aside className="live-talk-drawer" style={cssVars} data-testid={`live-talk-drawer-${agent}`}>
        <header className="live-talk-header">
          <div className="live-talk-avatar">{initials}</div>
          <div className="live-talk-headtext">
            <div className="live-talk-name">{agent}</div>
            <div className="live-talk-sub">Live talk · TTS in {agent}'s voice</div>
          </div>
          <button className="live-talk-close" onClick={onClose} data-testid="live-talk-close" aria-label="Close">×</button>
        </header>

        <div className="live-talk-stream" ref={streamRef} data-testid="live-talk-stream">
          {messages.length === 0 && (
            <div className="live-talk-empty">
              Say hi or ask for the latest signals.<br />Press Enter to send · Esc to close
            </div>
          )}
          {messages.map((m) => (
            <div key={m.id} className={`live-talk-msg ${m.role}`} data-testid={`live-talk-msg-${m.role}`}>
              <div className="live-talk-msg-meta">
                {m.role === 'user' ? 'YOU' : agent.toUpperCase()}
                {m.role === 'agent' && m.fallback_used && <span style={{ color: '#fb7185' }}>· Provider fallback active</span>}
              </div>
              <div className="live-talk-msg-text">{m.text}</div>
              {m.role === 'agent' && m.text && (
                <div className="live-talk-msg-actions">
                  <button
                    className={`live-talk-action ${playingId === m.id ? 'playing' : ''}`}
                    onClick={() => (playingId === m.id ? stopAudio() : playTts(m.id, m.text))}
                    data-testid={`live-talk-tts-${m.id}`}
                  >
                    {playingId === m.id ? '◼ stop' : '▶ play'}
                  </button>
                </div>
              )}
            </div>
          ))}
          {busy && (
            <div className="live-talk-thinking" data-testid="live-talk-thinking">
              <span /><span /><span />
            </div>
          )}
        </div>

        <div className="live-talk-tools">
          <label className="live-talk-toggle-tts">
            <input
              type="checkbox"
              checked={autoTts}
              onChange={(e) => setAutoTts(e.target.checked)}
              data-testid="live-talk-tts-toggle"
            />
            auto-speak
          </label>
          {lastModel && <span className="live-talk-model">{lastModel}</span>}
          <span className={`live-talk-speaking-state ${speakingState}`}>voice: {speakingState}</span>
        </div>

        <div className="live-talk-input-row">
          <textarea
            ref={inputRef}
            className="live-talk-input"
            placeholder={`Talk to ${agent}…`}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={busy}
            rows={1}
            data-testid="live-talk-input"
          />
          <button
            className="live-talk-send"
            onClick={send}
            disabled={busy || !input.trim()}
            data-testid="live-talk-send"
          >
            send
          </button>
        </div>
      </aside>
    </>
  );
}
